import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import * as Sentry from '@sentry/react'

const initialState = {
  items: [],
  pagination: {
    totalOrders: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  page: 1,
  limit: 200,
  calculatedTotals: {},
  isLoading: false,
  error: '',
  requestKey: null,
  requestId: null,
  totalsRequestId: null,
}

const orderedProductsSlice = createSlice({
  name: 'orderedProducts',
  initialState,
  reducers: {
    startOrderedProductsRequest: (state, { payload }) => {
      if (state.requestKey !== payload.requestKey) {
        state.items = []
        state.calculatedTotals = {}
        state.pagination = initialState.pagination
      }
      state.requestKey = payload.requestKey
      state.requestId = payload.requestId
      if (payload.page === 1) state.totalsRequestId = payload.requestId
      state.page = payload.page
      state.limit = payload.limit
      state.isLoading = true
      state.error = ''
    },
    setOrderedProducts: (state, action) => {
      state.items = action.payload
    },
    setOrderedProductsPagination: (state, action) => {
      state.pagination = action.payload
    },
    setOrderedProductsPage: (state, action) => {
      state.page = action.payload
    },
    setOrderedProductsLimit: (state, action) => {
      state.limit = action.payload
    },
    setOrderedProductsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setOrderedProductsError: (state, action) => {
      state.error = action.payload
    },
    setOrderedProductsCalculatedTotals: (state, action) => {
      state.calculatedTotals = action.payload
    },
    resetOrderedProducts: () => initialState,
  },
})

export const {
  startOrderedProductsRequest,
  setOrderedProducts,
  setOrderedProductsPagination,
  setOrderedProductsPage,
  setOrderedProductsLimit,
  setOrderedProductsLoading,
  setOrderedProductsError,
  resetOrderedProducts,
  setOrderedProductsCalculatedTotals,
} = orderedProductsSlice.actions

// Shared by the table and CSV so exports use exactly the same API filters.
export const getOrderedProducts = async ({
  venueId,
  start,
  end,
  page = 1,
  limit = 200,
  suppliers = [],
  types = [],
}) => {
  const normalizedStart = start.split('/').reverse().join('-')
  const normalizedEnd = end.split('/').reverse().join('-')
  return API.post(
    'api2',
    `orders/${venueId}/ordered-products?start=${normalizedStart}&end=${normalizedEnd}&page=${page}&limit=${limit}`,
    { body: { suppliers, types } },
  )
}

export const getAllOrderedProducts = async filters => {
  const firstPage = await getOrderedProducts({
    ...filters,
    page: 1,
    limit: 200,
  })
  const orders = [...(firstPage.orders || [])]
  for (let page = 2; page <= firstPage.pagination.totalPages; page += 1) {
    const response = await getOrderedProducts({ ...filters, page, limit: 200 })
    orders.push(...(response.orders || []))
  }
  return orders
}

let nextRequestId = 0

export const fetchOrderedProducts =
  ({
    venueId,
    start,
    end,
    page = 1,
    limit = 200,
    suppliers = [],
    types = [],
  }) =>
  async (dispatch, getState) => {
    const requestKey = JSON.stringify({
      venueId,
      start,
      end,
      limit,
      suppliers,
      types,
    })
    // A filter/venue change always starts at page 1, including when the
    // previous report was left on a later page.
    const requestedPage =
      getState().orderedProducts.requestKey === requestKey ? parseInt(page) : 1
    const requestId = ++nextRequestId
    dispatch(
      startOrderedProductsRequest({
        requestKey,
        requestId,
        page: requestedPage,
        limit: parseInt(limit),
      }),
    )
    try {
      const response = await getOrderedProducts({
        venueId,
        start,
        end,
        page: requestedPage,
        limit,
        suppliers,
        types,
      })
      // Page-one totals still belong to this report if the user has moved
      // to another page while they were loading. Only accept the latest
      // totals request, so older filters/refreshes cannot overwrite them.
      if (getState().orderedProducts.totalsRequestId === requestId) {
        dispatch(
          setOrderedProductsCalculatedTotals(response.calculatedTotals || {}),
        )
        dispatch(setOrderedProductsPagination(response.pagination || {}))
      }
      // Rows and loading state belong only to the latest page request.
      if (getState().orderedProducts.requestId !== requestId) return
      dispatch(setOrderedProducts(response.orders || []))
      dispatch(setOrderedProductsLoading(false))
    } catch (error) {
      if (getState().orderedProducts.requestId !== requestId) return
      console.error('fetchOrderedProducts error:', error)
      Sentry.captureException(error)
      dispatch(
        setOrderedProductsError(
          error.message || 'Failed to fetch ordered products',
        ),
      )
      dispatch(setOrderedProductsLoading(false))
    }
  }

export default orderedProductsSlice
